import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input } from 'reactstrap';
import Loader from 'components/Loader';
import __ from 'utils/i18n';
import { useSelector } from 'react-redux';
import { fetchResourceRP } from '../../../_shared/fetchResourceRP';
import { getMerchantMe, updateMerchantMe } from '../../_shared/resources';
import { notification } from 'utils/notifications';
import { BackendRpErrorResponse } from '../../../_shared/BackendRpErrorResponse';
import emptyEmailsIllustration from 'assets/merchant-config-empty-emails.png';

const MAX_EMAILS = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_FREQUENCY = 'daily';
const DEFAULT_TYPE = 'both';
const FREQUENCY_VALUES = ['daily', 'weekly', 'monthly'];
const TYPE_VALUES = ['both', 'succeeded', 'failed'];

const normalizeEmail = (email) => email.trim().toLowerCase();

export const MerchantConfigPanel = () => {
  const { token } = useSelector((state) => state.api);
  const user = useSelector((state) => state.login.user);

  const [emails, setEmails] = useState([]);
  const [frequency, setFrequency] = useState(DEFAULT_FREQUENCY);
  const [reportType, setReportType] = useState(DEFAULT_TYPE);
  const [savedFrequency, setSavedFrequency] = useState(DEFAULT_FREQUENCY);
  const [savedReportType, setSavedReportType] = useState(DEFAULT_TYPE);
  const [newEmail, setNewEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(null);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [deletingEmail, setDeletingEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notProvisioned, setNotProvisioned] = useState(false);

  const bootstrap = {
    merchantName: user?.accountName || '',
    merchantFiscalId: user?.fiscalId || '',
  };

  const applyMerchant = (data, fallbackEmails, fallbackFrequency, fallbackType) => {
    const nextFrequency = data?.reportFrequency || fallbackFrequency;
    const nextType = data?.reportType || fallbackType;
    setEmails(data?.emails || fallbackEmails);
    setFrequency(nextFrequency);
    setReportType(nextType);
    setSavedFrequency(nextFrequency);
    setSavedReportType(nextType);
  };

  const settingsDirty =
    frequency !== savedFrequency || reportType !== savedReportType;

  const loadEmails = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await fetchResourceRP(getMerchantMe(token));
      applyMerchant(data, [], DEFAULT_FREQUENCY, DEFAULT_TYPE);
      setNotProvisioned(false);
    } catch (error) {
      if (error?.status_code === 404 || error?.message_code === 'MERCHANT_NOT_PROVISIONED') {
        setEmails([]);
        setFrequency(DEFAULT_FREQUENCY);
        setReportType(DEFAULT_TYPE);
        setSavedFrequency(DEFAULT_FREQUENCY);
        setSavedReportType(DEFAULT_TYPE);
        setNotProvisioned(true);
      } else {
        notification.error(BackendRpErrorResponse.firstErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const persistConfig = async ({
    nextEmails = emails,
    nextFrequency = frequency,
    nextType = reportType,
  }) => {
    setSaving(true);
    try {
      const data = await fetchResourceRP(
        updateMerchantMe(
          {
            report_emails: nextEmails,
            report_frequency: nextFrequency,
            report_type: nextType,
          },
          bootstrap,
          token,
        ),
      );
      applyMerchant(data, nextEmails, nextFrequency, nextType);
      setNotProvisioned(false);
      notification.success(__('dashboard.sections.recurringPayments.plans.merchantConfig.saveSuccess'));
    } catch (error) {
      notification.error(BackendRpErrorResponse.firstErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // Email mutations keep the last saved frequency/type so draft select changes are not leaked.
  const persistEmails = async (nextEmails) =>
    persistConfig({
      nextEmails,
      nextFrequency: savedFrequency,
      nextType: savedReportType,
    });

  const handleSaveSettings = async () => {
    if (!settingsDirty) return;
    await persistConfig({ nextFrequency: frequency, nextType: reportType });
  };

  const validateNewEmail = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return __('dashboard.sections.recurringPayments.plans.merchantConfig.errors.required');
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      return __('dashboard.sections.recurringPayments.plans.merchantConfig.errors.invalidFormat');
    }
    if (emails.some((email) => normalizeEmail(email) === normalizeEmail(trimmed))) {
      return __('dashboard.sections.recurringPayments.plans.merchantConfig.errors.duplicate');
    }
    if (emails.length >= MAX_EMAILS) {
      return __('dashboard.sections.recurringPayments.plans.merchantConfig.errors.maxEmails', { max: MAX_EMAILS });
    }
    return null;
  };

  const validateEditEmail = (value, currentEmail) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return __('dashboard.sections.recurringPayments.plans.merchantConfig.errors.required');
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      return __('dashboard.sections.recurringPayments.plans.merchantConfig.errors.invalidFormat');
    }
    if (normalizeEmail(trimmed) === normalizeEmail(currentEmail)) {
      return __('dashboard.sections.recurringPayments.plans.merchantConfig.errors.unchanged');
    }
    if (
      emails.some(
        (email) =>
          normalizeEmail(email) === normalizeEmail(trimmed) &&
          normalizeEmail(email) !== normalizeEmail(currentEmail),
      )
    ) {
      return __('dashboard.sections.recurringPayments.plans.merchantConfig.errors.duplicate');
    }
    return null;
  };

  const handleAddEmail = async () => {
    const validationError = validateNewEmail(newEmail);
    if (validationError) {
      notification.error(validationError);
      return;
    }
    const nextEmails = [...emails, newEmail.trim()];
    setNewEmail('');
    await persistEmails(nextEmails);
  };

  const handleOpenEdit = (email) => {
    setDeletingEmail(null);
    setEditingEmail(email);
    setEditEmailValue('');
  };

  const handleCloseEdit = () => {
    setEditingEmail(null);
    setEditEmailValue('');
  };

  const handleOpenDelete = (email) => {
    setEditingEmail(null);
    setEditEmailValue('');
    setDeletingEmail(email);
  };

  const handleCloseDelete = () => {
    setDeletingEmail(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingEmail) return;

    const nextEmails = emails.filter((email) => email !== deletingEmail);
    await persistEmails(nextEmails);
    handleCloseDelete();
  };

  const handleSaveEdit = async () => {
    if (!editingEmail) return;

    const validationError = validateEditEmail(editEmailValue, editingEmail);
    if (validationError) {
      notification.error(validationError);
      return;
    }

    const nextEmails = emails.map((email) =>
      email === editingEmail ? editEmailValue.trim() : email,
    );

    await persistEmails(nextEmails);
    handleCloseEdit();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddEmail();
    }
  };

  const handleEditKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!validateEditEmail(editEmailValue, editingEmail)) {
        handleSaveEdit();
      }
    }
  };

  const canSaveEdit =
    !!editingEmail &&
    !saving &&
    !validateEditEmail(editEmailValue, editingEmail);

  if (loading) {
    return (
      <div className="merchant-config-panel merchant-config-panel--loading">
        <Loader />
      </div>
    );
  }

  return (
    <div className="merchant-config-panel">
      <div className="merchant-config-panel__main">
        <div className="merchant-config-panel__intro">
          <h3 className="merchant-config-panel__title">
            {__('dashboard.sections.recurringPayments.plans.merchantConfig.subtitle')}
          </h3>
          <p className="merchant-config-panel__description">
            {__('dashboard.sections.recurringPayments.plans.merchantConfig.description')}
          </p>
          {notProvisioned && (
            <p className="merchant-config-panel__hint">
              {__('dashboard.sections.recurringPayments.plans.merchantConfig.notProvisionedHint')}
            </p>
          )}
        </div>

        <div className="merchant-config-panel__settings">
          <div className="merchant-config-panel__field">
            <label className="merchant-config-panel__label" htmlFor="merchant-config-frequency">
              {__('dashboard.sections.recurringPayments.plans.merchantConfig.frequencyLabel')}
            </label>
            <select
              id="merchant-config-frequency"
              className="merchant-config-panel__input merchant-config-panel__select"
              value={frequency}
              onChange={(event) => setFrequency(event.target.value)}
              disabled={saving}
            >
              {FREQUENCY_VALUES.map((value) => (
                <option key={value} value={value}>
                  {__(`dashboard.sections.recurringPayments.plans.merchantConfig.frequency.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="merchant-config-panel__field">
            <label className="merchant-config-panel__label" htmlFor="merchant-config-type">
              {__('dashboard.sections.recurringPayments.plans.merchantConfig.typeLabel')}
            </label>
            <select
              id="merchant-config-type"
              className="merchant-config-panel__input merchant-config-panel__select"
              value={reportType}
              onChange={(event) => setReportType(event.target.value)}
              disabled={saving}
            >
              {TYPE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {__(`dashboard.sections.recurringPayments.plans.merchantConfig.type.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="merchant-config-panel__settings-actions">
            <Button
              type="button"
              color="primary"
              className="merchant-config-panel__save-settings-btn"
              onClick={handleSaveSettings}
              disabled={saving || !settingsDirty}
            >
              {__('dashboard.sections.recurringPayments.plans.merchantConfig.save')}
            </Button>
          </div>
        </div>

        <div className="merchant-config-panel__add">
          <label className="merchant-config-panel__label" htmlFor="merchant-config-new-email">
            {__('dashboard.sections.recurringPayments.plans.merchantConfig.newEmailLabel')}
          </label>
          <div className="merchant-config-panel__add-row">
            <Input
              id="merchant-config-new-email"
              type="email"
              className="merchant-config-panel__input"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={__('dashboard.sections.recurringPayments.plans.merchantConfig.emailPlaceholder')}
              disabled={saving || emails.length >= MAX_EMAILS}
            />
            <Button
              type="button"
              className="merchant-config-panel__add-btn"
              onClick={handleAddEmail}
              disabled={saving || emails.length >= MAX_EMAILS}
            >
              {__('dashboard.sections.recurringPayments.plans.merchantConfig.addEmail')}
            </Button>
          </div>
          <p className="merchant-config-panel__limit">
            {__('dashboard.sections.recurringPayments.plans.merchantConfig.emailsLimit', {
              max: MAX_EMAILS,
              count: emails.length,
            })}
          </p>
        </div>

        <div className="merchant-config-panel__list">
          {emails.length === 0 ? (
            <div className="merchant-config-panel__empty">
              <img
                className="merchant-config-panel__empty-illustration"
                src={emptyEmailsIllustration}
                alt=""
                aria-hidden="true"
              />
              <p className="merchant-config-panel__empty-text">
                {__('dashboard.sections.recurringPayments.plans.merchantConfig.emptyState')}
              </p>
            </div>
          ) : (
            <table className="merchant-config-panel__table">
              <thead>
                <tr>
                  <th>{__('dashboard.sections.recurringPayments.plans.merchantConfig.subscribedEmails')}</th>
                  <th>{__('dashboard.sections.recurringPayments.plans.merchantConfig.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <tr key={email}>
                    <td className="merchant-config-panel__email">{email}</td>
                    <td className="merchant-config-panel__actions">
                      <button
                        type="button"
                        className="merchant-config-panel__icon-btn"
                        onClick={() => handleOpenEdit(email)}
                        disabled={saving}
                        aria-label={__('dashboard.sections.recurringPayments.plans.merchantConfig.edit')}
                        title={__('dashboard.sections.recurringPayments.plans.merchantConfig.edit')}
                      >
                        <i className="mdi mdi-pencil-outline" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="merchant-config-panel__icon-btn merchant-config-panel__icon-btn--danger"
                        onClick={() => handleOpenDelete(email)}
                        disabled={saving}
                        aria-label={__('dashboard.sections.recurringPayments.plans.merchantConfig.remove')}
                        title={__('dashboard.sections.recurringPayments.plans.merchantConfig.remove')}
                      >
                        <i className="mdi mdi-delete-outline" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {deletingEmail && (
        <div className="merchant-config-panel__edit" role="dialog" aria-modal="true">
          <div className="merchant-config-panel__edit-header">
            {__('dashboard.sections.recurringPayments.plans.merchantConfig.deleteTitle')}
            <button
              type="button"
              className="merchant-config-panel__edit-close"
              onClick={handleCloseDelete}
              aria-label={__('actions.close')}
            >
              <i className="mdi mdi-close mdi-24px" aria-hidden="true" />
            </button>
          </div>

          <div className="merchant-config-panel__edit-body">
            <div className="merchant-config-panel__current-email merchant-config-panel__current-email--compact">
              <i className="mdi mdi-email-outline" aria-hidden="true" />
              <span>{deletingEmail}</span>
            </div>

            <div className="merchant-config-panel__warning">
              <i className="mdi mdi-alert-outline merchant-config-panel__warning-icon" aria-hidden="true" />
              <div className="merchant-config-panel__warning-text">
                <p>
                  {__.bb('dashboard.sections.recurringPayments.plans.merchantConfig.deleteWarning', {
                    email: deletingEmail,
                  })}
                </p>
                <p>
                  {__('dashboard.sections.recurringPayments.plans.merchantConfig.deleteWarningDetail')}
                </p>
              </div>
            </div>
          </div>

          <div className="merchant-config-panel__edit-footer">
            <Button
              type="button"
              color="danger"
              className="merchant-config-panel__delete-btn"
              onClick={handleConfirmDelete}
              disabled={saving}
            >
              {__('dashboard.sections.recurringPayments.plans.merchantConfig.remove')}
            </Button>
          </div>
        </div>
      )}

      {editingEmail && (
        <div className="merchant-config-panel__edit" role="dialog" aria-modal="true">
          <div className="merchant-config-panel__edit-header">
            {__('dashboard.sections.recurringPayments.plans.merchantConfig.editTitle')}
            <button
              type="button"
              className="merchant-config-panel__edit-close"
              onClick={handleCloseEdit}
              aria-label={__('actions.close')}
            >
              <i className="mdi mdi-close mdi-24px" aria-hidden="true" />
            </button>
          </div>

          <div className="merchant-config-panel__edit-body">
            <div className="merchant-config-panel__current-email">
              <i className="mdi mdi-email-outline" aria-hidden="true" />
              <span>{editingEmail}</span>
            </div>

            <div className="merchant-config-panel__field">
              <label className="merchant-config-panel__label" htmlFor="merchant-config-edit-email">
                {__('dashboard.sections.recurringPayments.plans.merchantConfig.newAddressLabel')}
              </label>
              <Input
                id="merchant-config-edit-email"
                type="email"
                className="merchant-config-panel__input"
                value={editEmailValue}
                onChange={(event) => setEditEmailValue(event.target.value)}
                onKeyDown={handleEditKeyDown}
                placeholder={__('dashboard.sections.recurringPayments.plans.merchantConfig.emailPlaceholder')}
                disabled={saving}
                autoFocus
              />
            </div>
          </div>

          <div className="merchant-config-panel__edit-footer">
            <Button
              type="button"
              color="primary"
              className="merchant-config-panel__save-btn"
              onClick={handleSaveEdit}
              disabled={!canSaveEdit}
            >
              {__('dashboard.sections.recurringPayments.plans.merchantConfig.save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
